/**
 * FileManagementModal.js
 * This component sends updated data to the server and then triggers an app refresh.
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { mergeWithCoordinates } from '../../services/dataService';
import '../../styles/components/dialogs/FileManagementModal.css';

const normalizeHeaderValue = (value) =>
  (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();

const SHEET1_REQUIRED_CORE_COLUMNS = [
  'Customer',
  'Assigned to',
  'Country',
  'Time zone',
  'Tier',
  'Last booked conference',
  'Group size (INTEGER)',
  'Sales goal (INTEGER)',
  'Agency/Sponsor (STRING)',
  'Total Rooms',
  'Total RN',
  'Total Revenue',
  'Attend to only one conference'
];

const hasYearMetricTriplet = (normalizedHeaders) => {
  const yearMetricMap = new Map();

  normalizedHeaders.forEach((header) => {
    const match = header.match(/^(\d{4})\s(rooms|rn|revenue)$/i);
    if (!match) return;

    const [, year, metric] = match;
    if (!yearMetricMap.has(year)) {
      yearMetricMap.set(year, new Set());
    }
    yearMetricMap.get(year).add(metric.toLowerCase());
  });

  return Array.from(yearMetricMap.values()).some((metricSet) =>
    metricSet.has('rooms') && metricSet.has('rn') && metricSet.has('revenue')
  );
};

export const validateSheet1Headers = (normalizedHeaders) => {
  const missingCoreColumns = SHEET1_REQUIRED_CORE_COLUMNS
    .map((column) => normalizeHeaderValue(column))
    .filter((column) => !normalizedHeaders.includes(column));

  if (missingCoreColumns.length > 0) {
    return {
      isValid: false,
      message: `Sheet "Sheet1" is missing required column: "${SHEET1_REQUIRED_CORE_COLUMNS.find(col => normalizeHeaderValue(col) === missingCoreColumns[0])}"`
    };
  }

  const hasYearFlagColumn = normalizedHeaders.some((header) => /^\d{4}$/.test(header));
  if (!hasYearFlagColumn) {
    return {
      isValid: false,
      message: 'Sheet "Sheet1" must include at least one year flag column (YYYY).'
    };
  }

  if (!hasYearMetricTriplet(normalizedHeaders)) {
    return {
      isValid: false,
      message: 'Sheet "Sheet1" must include at least one complete yearly metric set: YYYY Rooms, YYYY RN, YYYY Revenue.'
    };
  }

  return { isValid: true };
};

/**
 * NOTE: This modal uses React Portal to render at document.body level.
 * This avoids stacking context bugs caused by CSS filter on parent containers (e.g., .app-container.history-mode).
 */
const FileManagementModal = ({
  isOpen,
  onClose,
  clientData = [],
  pinnedClients = [],
  managerList = [],
}) => {

  // --- State Management ---
  const [selectedManager, setSelectedManager] = useState('');
  const [createBackup, setCreateBackup] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  
  // Import data states
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [showFileUploadDialog, setShowFileUploadDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [importError, setImportError] = useState('');
  const [importProgress, setImportProgress] = useState({ stage: '', progress: 0 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileValidationError, setFileValidationError] = useState('');

  // --- Effects ---
  useEffect(() => {
    if (message && messageType !== 'success') { // Don't auto-clear the final success message
      const timer = setTimeout(() => {
        setMessage('');
        setMessageType('info');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, messageType]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedManager('');
      setCreateBackup(true);
      setIsProcessing(false);
      setMessage('');
      setMessageType('info');
    }
  }, [isOpen]);


  // --- Import Functions ---
  const handleImportClick = () => {
    setImportError('');
    setShowImportConfirm(true);
  };

  // After user confirms, show file upload dialog
  const handleShowFileUploadDialog = () => {
    setShowImportConfirm(false);
    setShowFileUploadDialog(true);
    setFileValidationError('');
    setSelectedFile(null);
  };

  // Cancel file upload dialog
  const handleFileUploadCancel = () => {
    setShowFileUploadDialog(false);
    setSelectedFile(null);
    setFileValidationError('');
  };

  // Handle file selection and validation
  const handleFileChange = async (e) => {
    setFileValidationError('');
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setFileValidationError('Only Excel files (.xlsx, .xls) are allowed.');
      setSelectedFile(null);
      return;
    }

    // Read file using exceljs (secure replacement for xlsx)
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const ExcelJS = await import('exceljs');
        const arrayBuffer = evt.target.result;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        // Required sheets and columns
        const requiredSheets = ['Sheet1', 'Sponzori vezani za klijenta'];
        const requiredColumns = {
          'Sheet1': SHEET1_REQUIRED_CORE_COLUMNS,
          'Sponzori vezani za klijenta': [
            'Customer rename', 'GroupName rename', 'No of partners'
          ]
        };

        // Validate sheets
        const sheetNames = workbook.worksheets.map(ws => ws.name);
        for (const sheet of requiredSheets) {
          if (!sheetNames.includes(sheet)) {
            setFileValidationError(`Missing required sheet: "${sheet}"`);
            setSelectedFile(null);
            return;
          }
        }

        // Validate columns for each sheet
        for (const sheet of requiredSheets) {
          const worksheet = workbook.getWorksheet(sheet);
          if (!worksheet) continue;
          
          // Get headers from first row
          const headerRow = worksheet.getRow(1);
          let headers = [];
          headerRow.eachCell({ includeEmpty: true }, (cell) => {
            headers.push(normalizeHeaderValue(cell.value));
          });

          if (sheet === 'Sheet1') {
            const sheetValidation = validateSheet1Headers(headers);
            if (!sheetValidation.isValid) {
              setFileValidationError(sheetValidation.message);
              setSelectedFile(null);
              return;
            }
          }
          
          for (const col of requiredColumns[sheet]) {
            const normalizedCol = normalizeHeaderValue(col);
            if (!headers.includes(normalizedCol)) {
              setFileValidationError(`Sheet "${sheet}" is missing required column: "${col}"`);
              setSelectedFile(null);
              return;
            }
          }
        }

        // If all checks pass
        setSelectedFile(file);
        setFileValidationError('');
      } catch (err) {
        setFileValidationError('Error reading Excel file: ' + err.message);
        setSelectedFile(null);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Proceed with import after validation
  const handleFileUploadProceed = async () => {
    if (!selectedFile) {
      setFileValidationError('Please select a valid Excel file.');
      return;
    }
    setShowFileUploadDialog(false);
    // Now trigger the real import with progress bar (as in original code)
    handleImportConfirm();
  };

  const handleImportConfirm = async () => {
    setShowImportConfirm(false);
    setImporting(true);
    setImportError('');
    setImportResult(null);
    setImportProgress({ stage: 'starting', progress: 0 });

    try {
      let isCompleted = false;
      
      // Pass the selected file to the merge function
      const result = await mergeWithCoordinates(selectedFile, true, (progressData) => {
        setImportProgress(progressData);
        
        // When progress reaches complete, mark as completed but keep progress bar visible
        if (progressData.stage === 'complete' && progressData.progress === 100) {
          isCompleted = true;
        }
      });
      
      if (result.success) {
        // Ensure we show 100% completion for at least 1 second before showing dialog
        if (!isCompleted) {
          setImportProgress({ stage: 'complete', progress: 100 });
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else {
          // If already completed, just wait a moment for the user to see 100%
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        setImportResult(result.report);
        setImporting(false);
        setShowCompletionDialog(true);
      } else {
        setImportError(result.message || 'Failed to update portfolio');
        setImporting(false);
      }
    } catch (error) {
      setImportError(error.message || 'An error occurred during update operation');
      setImporting(false);
    }
  };

  const handleCompletionOK = () => {
    setShowCompletionDialog(false);
    setMessage('Import completed successfully! The application will now refresh to load the updated data.');
    setMessageType('success');
    
    // Wait 2 seconds, then reload the page
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const handleImportCancel = () => {
    setShowImportConfirm(false);
  };

  // Helper function to get progress description
  const getProgressDescription = (stage) => {
    switch (stage) {
      case 'reading_excel': return 'Reading portfolio file...';
      case 'reading_csv': return 'Loading enhancement database...';
      case 'loading_mappings': return 'Loading data mappings...';
      case 'processing': return 'Processing and enhancing records...';
      case 'saving': return 'Saving updated portfolio...';
      case 'finalizing': return 'Finalizing update operation...';
      case 'complete': return 'Update completed successfully!';
      default: return 'Preparing update operation...';
    }
  };

  // --- Core Function ---
  const handleSaveChanges = async () => {
    // --- Validation ---
    if (!selectedManager) {
      setMessage('Please select a manager first.');
      setMessageType('error');
      return;
    }
     if (!clientData || clientData.length === 0) {
        setMessage('Error: The main client list is empty. Cannot save.');
        setMessageType('error');
        return;
    }
     if (!pinnedClients || pinnedClients.length === 0) {
      setMessage('There are no pinned clients to reassign.');
      setMessageType('error');
      return;
    }

    setIsProcessing(true);
    setMessage('Saving data to the server...');
    setMessageType('info');

    try {
      const pinnedClientNames = new Set(pinnedClients.map(p => p.client_name));

      const updatedFullClientList = clientData.map(client => {
        if (pinnedClientNames.has(client.client_name)) {
          return { ...client, conference_manager: selectedManager };
        }
        return client;
      });

      const payload = {
        clientData: updatedFullClientList,
        createBackup: createBackup,
      };

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/save-portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `Server responded with status: ${response.status}`);
      }

      // --- Success and Refresh Logic ---
      // 1. Set a final success message for the user.
      setMessage('Save successful! The application will now refresh to load the latest data.');
      setMessageType('success');

      // 2. Wait 3 seconds, then reload the page.
      setTimeout(() => {
        window.location.reload();
      }, 3000);


    } catch (err) {
      console.error("Error during server save process:", err);
      setMessage(`An error occurred: ${err.message}`);
      setMessageType('error');
      setIsProcessing(false); // Make sure to stop processing on error
    }
    // Note: We don't set isProcessing to false on success,
    // because the page will be reloading anyway.
  };


  // --- Render ---
  const getMessageClassName = () => {
    if (messageType === 'success') return 'message success-message';
    if (messageType === 'error') return 'message error-message';
    return 'message info-message';
  }

  if (!isOpen) return null;

  // Render modal using React Portal to avoid stacking context issues with parent filters
  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="file-management-modal">
        <div className="modal-header">
          <h2>Save & Reassign Portfolio</h2>
          <button className="close-btn" onClick={onClose} disabled={isProcessing}>×</button>
        </div>

        {message && (
          <div className={getMessageClassName()}>{message}</div>
        )}

        <div className="tab-content">
          <div className="section">
            <h3>Update Portfolio Data</h3>
            <p>Load the latest enhanced portfolio file with updated information. This operation replaces your current portfolio with the newest version containing additional data fields and improvements.</p>
            
            <div className="import-features">
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Updates portfolio with latest enhanced data</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Adds additional data fields and information</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Creates automatic backup before updating</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Comprehensive data validation and logging</span>
              </div>
            </div>

            <div className="import-actions">
              <button
                type="button"
                className="confirm-cancel distribution-report-btn"
                onClick={handleDistributionReport}
                disabled={importing || isProcessing}
                title="Generate Distribution Report Excel"
              >
                Distribution Report
              </button>
              <button 
                type="button" 
                className="import-btn"
                onClick={handleImportClick}
                disabled={importing || isProcessing}
                title="Update portfolio with latest data"
              >
                {importing ? (
                  <>
                    <div className="spinner"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7,10 12,15 17,10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Update/Reset Portfolio</span>
                  </>
                )}
              </button>
            </div>

            {importing && (
              <div className="import-progress">
                <div className="progress-header">
                  <span className="progress-icon">⏳</span>
                  <span className="progress-text">{getProgressDescription(importProgress.stage)}</span>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-bar-fill" 
                      style={{ width: `${importProgress.progress || 0}%` }}
                    ></div>
                  </div>
                  <div className="progress-percentage">
                    {Math.round(importProgress.progress || 0)}%
                    {importProgress.current && importProgress.total && (
                      <span className="progress-detail"> ({importProgress.current}/{importProgress.total})</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {importError && (
              <div className="import-error">
                <span className="error-icon">⚠️</span>
                <span>{importError}</span>
              </div>
            )}

            {importResult && (
              <div className="import-result">
                <div className="result-header">
                  <span className="success-icon">✅</span>
                  <span>Import completed successfully!</span>
                </div>
                <div className="result-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Rows:</span>
                    <span className="stat-value">{importResult.totalRows}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Imported:</span>
                    <span className="stat-value success">{importResult.matchedRows}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">No Match:</span>
                    <span className="stat-value warning">{importResult.unmatchedRows}</span>
                  </div>
                </div>
                {importResult.unmatchedCountries && importResult.unmatchedCountries.length > 0 && (
                  <div className="unmatched-countries">
                    <div className="unmatched-title">Countries without coordinates:</div>
                    <div className="unmatched-list">
                      {importResult.unmatchedCountries.map((country, index) => (
                        <div key={index} className="unmatched-item">
                          <span className="country-name">{country.name}</span>
                          <span className="country-count">({country.count} record{country.count > 1 ? 's' : ''})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="section no-border">
            <h3>1. Reassign Pinned Clients</h3>
            <p>Select a manager to assign all <strong>{pinnedClients.length}</strong> pinned clients to.</p>
            <select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              className="manager-select"
              disabled={isProcessing}
            >
              <option value="" disabled>-- Select a Conference Manager --</option>
              {managerList.map(manager => (
                <option key={manager} value={manager}>{manager}</option>
              ))}
            </select>
          </div>

          <div className="section no-border">
            <h3>2. Save to Server</h3>
            <div className="save-note">
              <strong>Note:</strong> This will overwrite the `protfolio.xlsx` file on the server.
            </div>
            <div className="backup-option">
               <input
                type="checkbox"
                id="create-backup"
                checked={createBackup}
                onChange={(e) => setCreateBackup(e.target.checked)}
                disabled={isProcessing}
              />
              <label htmlFor="create-backup" className="backup-label">Create a timestamped backup before saving</label>
            </div>
            <div className="save-actions">
              <button
                className="import-btn"
                disabled={isProcessing || !selectedManager || pinnedClients.length === 0}
                onClick={handleSaveChanges}
                title="Save changes and refresh app"
              >
                {isProcessing ? (
                  <>
                    <div className="spinner"></div>
                    <span>Saving and Refreshing...</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7,10 12,15 17,10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Save & Refresh</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Import Confirmation Dialog */}
        {showImportConfirm && (
          <div className="confirm-overlay">
            <div className="confirm-dialog">
              <div className="confirm-header">
                <h4>Confirm Portfolio Update</h4>
              </div>
              <div className="confirm-content">
                <p>Are you sure you want to update your portfolio with the latest enhanced data?</p>
                <div className="confirm-details">
                  <div className="confirm-item">
                    <span className="confirm-icon">📁</span>
                    <span>A backup will be created automatically</span>
                  </div>
                  <div className="confirm-item">
                    <span className="confirm-icon">📥</span>
                    <span>Portfolio will be updated with enhanced data fields</span>
                  </div>
                  <div className="confirm-item">
                    <span className="confirm-icon">📊</span>
                    <span>A detailed update report will be provided</span>
                  </div>
                  <div className="confirm-item">
                    <span className="confirm-icon">📋</span>
                    <span>Operation will be logged for audit purposes</span>
                  </div>
                </div>
              </div>
              <div className="confirm-actions">
                <button className="confirm-cancel" onClick={handleImportCancel}>
                  Cancel
                </button>
                <button className="confirm-proceed" onClick={handleShowFileUploadDialog}>
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* File Upload Dialog */}
        {showFileUploadDialog && (
          <div className="confirm-overlay">
            <div className="confirm-dialog">
              <div className="confirm-header">
                <h4>Upload Portfolio Excel File</h4>
              </div>
              <div className="confirm-content">
                <p>Please select an Excel file (.xlsx, .xls) that matches the required template structure.</p>
                <div className="file-upload-box">
                  <input
                    id="portfolio-file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    disabled={importing}
                    style={{ display: "none" }}
                  />
                  <span className="file-upload-filename">
                    {selectedFile ? selectedFile.name : "No file chosen"}
                  </span>
                  <label
                    htmlFor="portfolio-file-input"
                    className="confirm-cancel file-upload-btn"
                    tabIndex={0}
                  >
                    Choose File
                  </label>
                </div>
                {fileValidationError && (
                  <div className="import-error" style={{ marginTop: 12 }}>
                    <span className="error-icon">⚠️</span>
                    <span>{fileValidationError}</span>
                  </div>
                )}
              </div>
              <div className="confirm-actions">
                <button className="confirm-cancel" onClick={handleFileUploadCancel}>
                  Cancel
                </button>
                <button
                  className="confirm-proceed"
                  onClick={handleFileUploadProceed}
                  disabled={!selectedFile || !!fileValidationError}
                >
                  Upload & Update Portfolio
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Update Completion Dialog */}
        {showCompletionDialog && (
          <div className="confirm-overlay">
            <div className="confirm-dialog">
              <div className="confirm-header">
                <h4>Portfolio Update Complete!</h4>
              </div>
              <div className="confirm-content">
                <div className="completion-icon">🎉</div>
                <p>Your portfolio has been successfully updated with the latest enhanced data!</p>
                <div className="completion-stats">
                  {importResult && (
                    <>
                      <div className="completion-stat">
                        <strong>{importResult.totalRows}</strong> total records processed
                      </div>
                      <div className="completion-stat">
                        <strong>{importResult.matchedRows}</strong> records enhanced with additional data
                      </div>
                      {importResult.unmatchedRows > 0 && (
                        <div className="completion-stat warning">
                          <strong>{importResult.unmatchedRows}</strong> records pending enhancement
                        </div>
                      )}
                    </>
                  )}
                </div>
                <p><strong>The app will refresh to load the updated portfolio.</strong></p>
                <p>Please click "OK" to continue.</p>
              </div>
              <div className="confirm-actions">
                <button className="confirm-proceed completion-btn" onClick={handleCompletionOK}>
                  OK - Refresh App
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>,
    document.body
  );
};

/* --- Distribution Report Handler --- */
function handleDistributionReport() {
  // Open the Manager Performance Dashboard modal
  if (typeof window !== "undefined" && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent("openManagerPerformanceDashboard"));
  }
}

export default FileManagementModal;
